import React from 'react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {Page} from 'components/common/Page';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {DateRange} from 'components/ui-kit/DateRange';
import {Helper} from 'components/ui-kit/Helper';
import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {DECLARATIONS_STATUS_COLORS} from 'core/constants/statuses';
import {GdsStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {GridFieldConfig} from 'types';
import {
    DeclarationsAccessors,
    DeclarationsGridFormattedRow,
    DeclarationsGridRawRow,
    declarationStatuses,
} from 'types/declarations';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';

import {ControlsWrapper, HeaderControls} from './styled';
import {DeclarationsGridTable} from './Table';
import {TableSearch} from './Table/SearchFilter';

export function convertData(
    data: DeclarationsGridRawRow[] | undefined,
    t: TFunction,
): DeclarationsGridFormattedRow[] {
    if (!data) return [];

    return data.map((elem) => ({
        [DeclarationsAccessors.id]: (
            <Link to={{pathname: generatePath(ROUTE.DECLARATION_DETAILS, {id: elem.id})}}>
                {elem.id}
            </Link>
        ),
        [DeclarationsAccessors.customsGdNumber]: handleNil(elem.customsGdNumber),
        [DeclarationsAccessors.customsCode]: handleNil(elem.customsCode),
        [DeclarationsAccessors.receiptedAt]: formatDate(elem.receiptedAt, 'dd.MM.yyyy hh:mm'),
        [DeclarationsAccessors.participantTin]: handleNil(elem.participantTin),
        [DeclarationsAccessors.participantName]: handleNil(elem.participantName),
        [DeclarationsAccessors.status]: (
            <>
                <StatusWrapper color={DECLARATIONS_STATUS_COLORS[elem.status]}>
                    {t('dictionaries.declarationStatuses.' + elem.status)}
                </StatusWrapper>
                {elem.status === GdsStatus.REJECTED_AUTOMATIC && (
                    <Helper
                        content={t('pages.documents.gds.statusRejectedAutomaticHelper', {
                            defaultValue: 'Rejected automatically due to code disaggregation',
                        })}
                    />
                )}
            </>
        ),
    }));
}

const getGridColumns = (t: TFunction): GridFieldConfig[] => [
    {
        id: DeclarationsAccessors.id,
        header: t('pages.declarations.fields.id'),
        isSortable: true,
        width: 150,
    },
    {
        id: DeclarationsAccessors.customsGdNumber,
        header: t('pages.declarations.fields.customsGdNumber'),
        isSortable: true,
        width: 150,
    },
    {
        id: DeclarationsAccessors.customsCode,
        header: t('pages.declarations.fields.customsCode'),
        isSortable: true,
        width: 150,
    },
    {
        id: DeclarationsAccessors.receiptedAt,
        header: t('pages.declarations.fields.receiptedAt'),
        filter: <DateRange />,
        width: 150,
    },
    {
        id: DeclarationsAccessors.participantTin,
        header: t('pages.declarations.fields.participantTin'),
        isSortable: true,
        width: 150,
    },
    {
        id: DeclarationsAccessors.participantName,
        header: t('pages.declarations.fields.participantName'),
        isSortable: true,
    },
    {
        id: DeclarationsAccessors.status,
        header: t('pages.declarations.fields.status'),
        width: 150,
        filter: (
            <CheckboxArray
                name="declaration-statuses"
                options={() =>
                    declarationStatuses.map((s) => ({
                        id: s,
                        value: s,
                        label: s.toLowerCase(),
                    }))
                }
            />
        ),
    },
];

export const DeclarationsPage = () => {
    const {t} = useTranslation();

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
            </ControlsWrapper>
        </HeaderControls>
    );

    return (
        <Page title={t('menu.Declarations')}>
            <DeclarationsGridTable
                configFunc={getGridColumns}
                dataMapper={convertData}
                headerControls={headerControls}
            />
        </Page>
    );
};
